// The UI atoms — TODO T17 (30 Aug 2026), rebuilt to v2 at T30 (1 Sep 2026).
//
// docs/console-demo/README.md is explicit about the split: the atoms carry over "as designs to
// reimplement", the code does not. So the shapes, the stroke weights and the proportions below
// are the mockup's (`docs/console-demo/mvp/ui.jsx`, which is Mona's v2 with the MVP's subset) and
// the delivery is not — classes from `styles/app.css` rather than a style object per element.
//
// TWO OF THESE WERE BUILT AHEAD OF THEIR SCREEN, and deliberately: `Sparkline` is the shape
// Reports draws (T32) and `ActivityItem` is the searches-home feed — drawn at T30, fed at T36
// (screens/ActivityRail.tsx). Design doc § T30.4 asks for both here, so the two lanes that need
// them add a screen rather than a visual vocabulary. `Sparkline` still renders nowhere, which is
// the difference between an unused component and a dead button — a recruiter cannot see it
// promising something.
//
// What is NOT here, and will not be: `SubScore`, `Waveform`, `LiveWave` — the sub-scores,
// sentiment and voice-signal analysis, which are out until C5 answers and some of them out for
// good (design doc § 1.3). `ScoreRing` is not here as the kadmon score either; `ui/Rating.tsx`
// draws the same ring as the RATING a recruiter set by hand (T41, § 1.3 as amended 5 Sep 2026):
// a number a person typed is judgement recorded in a field, and it is labelled as theirs.
import { Children, cloneElement, Fragment, isValidElement } from 'react';
import type { PointerEvent as ReactPointerEvent, ReactElement, ReactNode } from 'react';
import type { ActivityKind } from '@vera/bff';
import { initials as toInitials } from '../format.ts';

export type IconName =
  | 'search'
  | 'inbox'
  | 'users'
  | 'shield'
  | 'chart'
  | 'settings'
  | 'doc'
  | 'wa'
  | 'phone'
  | 'pause'
  | 'play'
  | 'check'
  | 'x'
  | 'alert'
  | 'chevron'
  | 'chevron-d'
  | 'arrow'
  | 'plus'
  | 'filter'
  | 'compare'
  | 'globe'
  | 'lock'
  | 'unlock'
  | 'circle'
  | 'mic'
  | 'more'
  | 'menu'
  | 'sparkle'
  | 'link'
  | 'mail'
  | 'clock'
  | 'sun'
  | 'moon'
  | 'send'
  | 'trash'
  | 'tag'
  | 'building'
  | 'logout'
  | 'clip'
  | 'eye';

/** Stroke only, 16px grid, currentColor — v2's icon language, verbatim. */
export function Icon({
  name,
  size = 16,
  stroke = 1.5,
  className,
}: {
  name: IconName;
  size?: number;
  stroke?: number;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      className={className}
      style={{ display: 'inline-block', verticalAlign: '-2px', flexShrink: 0 }}
      aria-hidden
    >
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {PATHS[name]}
      </g>
    </svg>
  );
}

const PATHS: Record<IconName, ReactNode> = {
  search: (
    <>
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5L13.5 13.5" />
    </>
  ),
  inbox: (
    <>
      <path d="M2 9v3.5a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V9" />
      <path d="M2 9l1.5-5.5a1 1 0 0 1 1-.75h7a1 1 0 0 1 1 .75L14 9" />
      <path d="M2 9h3l1 1.5h4l1-1.5h3" />
    </>
  ),
  users: (
    <>
      <circle cx="6" cy="5.5" r="2.5" />
      <path d="M2 13.5c0-2.2 1.8-4 4-4s4 1.8 4 4" />
      <path d="M10.5 5.5a2 2 0 1 1 0 4M14 13.5c0-1.7-1-3.2-2.5-3.8" />
    </>
  ),
  shield: (
    <>
      <path d="M8 1.5L2.5 3.5v4c0 3.5 2.5 6 5.5 7 3-1 5.5-3.5 5.5-7v-4L8 1.5z" />
      <path d="M6 8l1.5 1.5L10.5 6.5" />
    </>
  ),
  chart: (
    <>
      <path d="M2 13.5h12" />
      <path d="M4 11V8M7 11V4M10 11V6M13 11V9" />
    </>
  ),
  settings: (
    <>
      <circle cx="8" cy="8" r="2" />
      <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3 3l1.5 1.5M11.5 11.5L13 13M3 13l1.5-1.5M11.5 4.5L13 3" />
    </>
  ),
  doc: (
    <>
      <path d="M3.5 1.5h6L13 5v9.5H3.5V1.5z" />
      <path d="M9.5 1.5V5H13" />
      <path d="M5.5 8h5M5.5 10.5h5M5.5 12h3" />
    </>
  ),
  wa: (
    <>
      <path d="M2 14l1-3.5A6 6 0 1 1 5.5 13L2 14z" />
      <path d="M6 6.5c0 .5.2 1.2.7 1.8.5.6 1.2 1 1.7 1.2.3.1.8 0 1.1-.3l.3-.3a.3.3 0 0 1 .4 0l.7.7c.1.1.1.3 0 .4-.4.4-.9.7-1.4.6-1.4-.2-3.4-1.5-3.8-3.5 0-.5.2-1 .6-1.4.1-.1.3-.1.4 0l.7.7c.1.1.1.3 0 .4l-.3.3c-.3.3-.3.7-.1 1z" />
    </>
  ),
  phone: (
    <path d="M3 2.5h2l1 3-1.5 1A8 8 0 0 0 8.5 11l1-1.5 3 1v2c0 .5-.4 1-1 1A11 11 0 0 1 2 3.5c0-.5.4-1 1-1z" />
  ),
  pause: <path d="M5 3v10M11 3v10" />,
  play: <path d="M4.5 3v10l8-5-8-5z" />,
  check: <path d="M3 8.5L6.5 12L13 4.5" />,
  x: <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />,
  alert: (
    <>
      <path d="M8 2L1.5 13.5h13L8 2z" />
      <path d="M8 6.5v3M8 11.5v.01" />
    </>
  ),
  chevron: <path d="M5.5 3.5l5 4.5-5 4.5" />,
  'chevron-d': <path d="M3.5 5.5l4.5 5 4.5-5" />,
  arrow: (
    <>
      <path d="M2.5 8h11" />
      <path d="M9 3.5L13.5 8 9 12.5" />
    </>
  ),
  plus: <path d="M8 2v12M2 8h12" />,
  filter: <path d="M2 3h12M4 7.5h8M6.5 12h3" />,
  compare: (
    <>
      <path d="M8 1.5v13" />
      <path d="M2 4.5v7M14 4.5v7" />
      <path d="M2 4.5h5M9 4.5h5M2 11.5h5M9 11.5h5" />
    </>
  ),
  globe: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M2 8h12M8 2c1.8 2 2.8 4 2.8 6S9.8 14 8 14M8 2C6.2 4 5.2 6 5.2 8s1 4 2.8 6" />
    </>
  ),
  lock: (
    <>
      <rect x="3" y="7" width="10" height="7" rx="1" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
    </>
  ),
  unlock: (
    <>
      <rect x="3" y="7" width="10" height="7" rx="1" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 4.8-1" />
    </>
  ),
  circle: <circle cx="8" cy="8" r="6" />,
  mic: (
    <>
      <rect x="6" y="2" width="4" height="8" rx="2" />
      <path d="M3.5 8a4.5 4.5 0 0 0 9 0M8 12.5V14" />
    </>
  ),
  more: (
    <>
      <circle cx="4" cy="8" r="0.5" />
      <circle cx="8" cy="8" r="0.5" />
      <circle cx="12" cy="8" r="0.5" />
    </>
  ),
  menu: <path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h11" />,
  // T83: the paper-clip that attaches a list to a question in the kadmon panel.
  clip: (
    <path d="M14.29 7.37l-6.13 6.13a4 4 0 0 1-5.66-5.66l6.13-6.13a2.67 2.67 0 0 1 3.77 3.77l-6.13 6.13a1.33 1.33 0 0 1-1.89-1.89l5.66-5.65" />
  ),
  sparkle: <path d="M8 1.5L9.5 6.5L14.5 8L9.5 9.5L8 14.5L6.5 9.5L1.5 8L6.5 6.5z" />,
  link: (
    <>
      <path d="M7 9c0.8 1 2.5 1 3.5 0L13 6.5c1-1 1-2.5 0-3.5s-2.5-1-3.5 0L8 4.5" />
      <path d="M9 7c-0.8-1-2.5-1-3.5 0L3 9.5c-1 1-1 2.5 0 3.5s2.5 1 3.5 0L8 11.5" />
    </>
  ),
  mail: (
    <>
      <rect x="2" y="3.5" width="12" height="9" rx="1.5" />
      <path d="M2.5 4.5L8 9l5.5-4.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.5V8l2.5 1.5" />
    </>
  ),
  sun: (
    <>
      <circle cx="8" cy="8" r="3" />
      <path d="M8 1.5v1.5M8 13v1.5M1.5 8H3M13 8h1.5M3.4 3.4l1 1M11.6 11.6l1 1M3.4 12.6l1-1M11.6 4.4l1-1" />
    </>
  ),
  moon: <path d="M13 9.5A5.5 5.5 0 0 1 6.5 3a5.5 5.5 0 1 0 6.5 6.5z" />,
  send: (
    <>
      <path d="M2.5 8L13.5 2.5 10 13.5 8 9z" />
      <path d="M8 9l5.5-6.5" />
    </>
  ),
  trash: (
    <>
      <path d="M3 4.5h10M6.5 4.5V3h3v1.5M4 4.5l.7 9h6.6l.7-9" />
    </>
  ),
  tag: (
    <>
      <path d="M2 2h5.5L14 8.5 8.5 14 2 7.5z" />
      <circle cx="5" cy="5" r="1" />
    </>
  ),
  building: (
    <>
      <rect x="3" y="2" width="10" height="12" rx="1" />
      <path d="M6 5h1M9 5h1M6 8h1M9 8h1M6 11h1M9 11h1" />
    </>
  ),
  logout: (
    <>
      <path d="M6 2.5H3.5v11H6" />
      <path d="M9 5l3 3-3 3M12 8H6" />
    </>
  ),
  eye: (
    <>
      <path d="M1.5 8s2.5-4.5 6.5-4.5S14.5 8 14.5 8s-2.5 4.5-6.5 4.5S1.5 8 1.5 8z" />
      <circle cx="8" cy="8" r="2" />
    </>
  ),
};

/**
 * The firm's name as it is written: kadmon, in lower case everywhere (T64, Natanel) — inside the
 * labels this brain sets in capitals too, which is the one place the source's lower case would
 * not survive. `harness/screens.ts` checks the drawn text on every screen. Named for what it is
 * rather than after the firm: packages/db/src/company-name.test.ts refuses the name with a capital
 * anywhere, and a component's name must start with one.
 */
export function FirmName() {
  return <span className="firm-name">kadmon</span>;
}

/**
 * T100 (25 Sep 2026): a string drawn in a label set in capitals, with the firm's name kept in
 * lower case (`<FirmName />`). Card titles, field labels and (T226) a card's header action pass
 * through this; since the AI took the name, many of them carry it. What it reaches: `walk`.
 */
export function withFirmName(text: ReactNode): ReactNode {
  return walk(text);
}

/**
 * One walk, down strings, arrays, other iterables and the page's own elements, at any depth;
 * never inside a component (its props are its own contract, T226a). A node the name is not in
 * comes back as the same object, and an element is cloned with its children as it wrote them
 * (varargs, so the siblings keep the keys they had; `Children.map` would re-key every one,
 * T226a). A changed array or iterable comes back as a new array. The exception is an iterable
 * that can be read again only if it is a `Set` or a `Map`: any other (a generator, an iterable
 * handing out a one-shot iterator) was spread once, so it always comes back as an array, and
 * the walk opens one iterator on it, never a probe's second (T226b, T226c, T226d).
 */
function walk(node: unknown): ReactNode {
  if (typeof node === 'string') {
    if (!node.includes('kadmon')) return node;
    return node
      .split(/(kadmon)/)
      .map((part, i) => (part === 'kadmon' ? <FirmName key={i} /> : part));
  }
  if (Array.isArray(node)) return walkAll(node) ?? (node as ReactNode);
  if (isPageElement(node)) {
    const { children } = node.props;
    if (Array.isArray(children)) {
      const kids = walkAll(children);
      return kids === undefined ? node : cloneElement(node, undefined, ...kids);
    }
    const kid = walk(children);
    return kid === children ? node : cloneElement(node, undefined, kid);
  }
  if (isIterable(node)) {
    const items = [...node];
    const readAgain = node instanceof Set || node instanceof Map;
    return walkAll(items) ?? (readAgain ? (node as ReactNode) : (items as ReactNode[]));
  }
  return node as ReactNode;
}

/** The nodes walked, or undefined when none changed, so the caller keeps its own object. */
function walkAll(nodes: readonly unknown[]): ReactNode[] | undefined {
  const kids = nodes.map(walk);
  return kids.every((kid, i) => kid === nodes[i]) ? undefined : kids;
}

/** Anything React would draw as a list but an array: a Set, a generator. Not a string, handled first. */
function isIterable(node: unknown): node is Iterable<unknown> {
  return typeof node === 'object' && node !== null && Symbol.iterator in node;
}

/** Elements whose content HTML keeps as text only, so a `<span>` inside one would be lost. */
const TEXT_ONLY = new Set(['option', 'textarea', 'title', 'style', 'script']);

/**
 * One of the page's own elements (a `span`, a `b`) or a fragment, whose children are nodes the
 * page draws: a component's children are its own to read (one child, a function, an object), so
 * the walk never goes into one (T226's review).
 */
function isPageElement(node: unknown): node is ReactElement<{ children?: unknown }> {
  if (!isValidElement(node)) return false;
  if (node.type === Fragment) return true;
  return typeof node.type === 'string' && !TEXT_ONLY.has(node.type);
}

/** kadmon's presence. The demo's abstract orb — the product has no avatar and should not grow one. */
export function VeraOrb({ size = 14, active = true }: { size?: number; active?: boolean }) {
  return (
    <span
      className={active ? 'orb' : 'orb orb--idle'}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <span className="orb__core" />
    </span>
  );
}

/** Initials on a flat disc (T392a: the light look has no hue-gradient avatars; `.avatar` paints it). */
export function Avatar({ name, size = 28 }: { name: string | null; size?: number }) {
  const letters = toInitials(name);
  return (
    <span
      className="avatar"
      style={{ width: size, height: size, fontSize: size * 0.44 }}
      aria-hidden
    >
      {letters}
    </span>
  );
}

export type Tone = 'neutral' | 'accent' | 'ok' | 'warn' | 'bad' | 'info' | 'ghost';

export function Badge({
  tone = 'neutral',
  dot = false,
  children,
}: {
  tone?: Tone;
  dot?: boolean;
  children: ReactNode;
}) {
  return (
    <span className={tone === 'neutral' ? 'badge' : `badge badge--${tone}`}>
      {dot && <span className="badge__dot" />}
      {children}
    </span>
  );
}

export function Card({
  title,
  action,
  children,
  flush = false,
  hoverable = false,
  accent = false,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  /** No body padding — for a card whose content draws its own edges (a thread, a table). */
  flush?: boolean;
  hoverable?: boolean;
  accent?: boolean;
}) {
  const classes = ['card', hoverable && 'card--hoverable', accent && 'card--accent']
    .filter(Boolean)
    .join(' ');
  return (
    <section className={classes}>
      {title && (
        <header className="card__header">
          <span className="label">{withFirmName(title)}</span>
          {withFirmName(action)}
        </header>
      )}
      <div className={flush ? 'card__body card__body--flush' : 'card__body'}>{children}</div>
    </section>
  );
}

export type ButtonKind = 'ghost' | 'primary' | 'accent' | 'quiet' | 'solid' | 'warn' | 'danger';

/**
 * The pill button.
 *
 * `kind` is the whole styling API on purpose — the mockup's buttons differ only by kind and size,
 * and a `style` prop here is how a screen starts inventing a seventh look.
 */
export function Button({
  children,
  onClick,
  onPointerDown,
  kind = 'ghost',
  size = 'md',
  icon,
  type = 'button',
  disabled,
  title,
  pressed,
  'aria-label': ariaLabel,
}: {
  children?: ReactNode;
  onClick?: () => void;
  /** The press itself, before the click: Talk starts its call on a mouse's (app/voice.tsx). */
  onPointerDown?: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  kind?: ButtonKind;
  size?: 'sm' | 'md';
  icon?: IconName;
  type?: 'button' | 'submit';
  disabled?: boolean;
  title?: string;
  /** A toggle's state (T141a: More like this), said to assistive tech as `aria-pressed`. */
  pressed?: boolean;
  'aria-label'?: string;
}) {
  const classes = ['btn', kind !== 'ghost' && `btn--${kind}`, size === 'sm' && 'btn--sm']
    .filter(Boolean)
    .join(' ');
  return (
    <button
      className={classes}
      type={type}
      onClick={onClick}
      onPointerDown={onPointerDown}
      disabled={disabled}
      title={title}
      aria-pressed={pressed}
      aria-label={ariaLabel}
    >
      {icon && <Icon name={icon} size={13} />}
      {children}
    </button>
  );
}

export function KV({ k, v, mono = false }: { k: string; v: ReactNode; mono?: boolean }) {
  return (
    <div className="kv">
      <span className="label">{withFirmName(k)}</span>
      <span className={mono ? 'kv__v mono' : 'kv__v'}>{v}</span>
    </div>
  );
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: string }) {
  return (
    <div className="stat">
      <div className="label">{withFirmName(label)}</div>
      <div className="stat__value">{value}</div>
      {sub && <div className="stat__sub">{sub}</div>}
    </div>
  );
}

/** The rule-and-caps eyebrow over a serif title — v2's section head. */
export function SectionHead({
  eyebrow,
  title,
  action,
}: {
  eyebrow?: string;
  title?: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-head">
      <div>
        {eyebrow && <div className="eyebrow">{withFirmName(eyebrow)}</div>}
        {title && <h2 className="section-head__title">{title}</h2>}
      </div>
      {action}
    </div>
  );
}

/**
 * "What kadmon knows" / "Only you see this" — the heading every candidate-facing group carries
 * (design doc § 1.5).
 *
 * The sentence under it is the point. Recruiter-only data is kept from the agent BY GRANT: the
 * console-owned tables carry no agent grant at all, and the couplings the agent may read are
 * enumerated and asserted by a live suite (packages/db/src/ownership.ts). The label is telling a
 * recruiter what the database already enforces, not making them a promise.
 */
export function GroupHead({ side }: { side: 'vera' | 'you' }) {
  const vera = side === 'vera';
  return (
    <div className={vera ? 'group-head group-head--vera' : 'group-head'}>
      <span className="eyebrow">
        <Icon name={vera ? 'unlock' : 'lock'} size={11} />
        {vera ? (
          <>
            What <FirmName /> knows
          </>
        ) : (
          'Only you see this'
        )}
      </span>
      <p>
        {vera
          ? 'Entered by the headhunter. kadmon reads this at the start of every conversation and may say any of it.'
          : 'For the team only. kadmon cannot read any of it: the database keeps it from her.'}
      </p>
    </div>
  );
}

/**
 * One line of the activity feed. Built at T30 in v2's language; the feed itself arrived at T36
 * (design doc § T36.1), and its kinds are the service's — `ActivityKind` in
 * `services/bff/src/activity.ts`, which is where a row becomes a line. `held` and `failed` are
 * the two send outcomes the mockup did not draw and the worker does write; `stage` was drawn here
 * before anything fed it and T32 fed it (`pipeline_stage_history`), which is also when the live
 * call's own notifications got the `call` kind.
 */
export function ActivityItem({
  kind,
  text,
  when,
  unread = false,
  to,
  action,
}: {
  kind: ActivityKind;
  text: string;
  when: string;
  unread?: boolean;
  /** Rendered by the caller — this atom draws the row and knows nothing about routing. */
  to?: ReactNode;
  /** T484c: what a recruiter can do about the line, or where that stands, under its time. */
  action?: ReactNode;
}) {
  const meta: Record<typeof kind, { icon: IconName; tone: string }> = {
    inbound: { icon: 'wa', tone: 'var(--ok)' },
    voice: { icon: 'mic', tone: 'var(--ok)' },
    client: { icon: 'building', tone: 'var(--info)' },
    gate: { icon: 'lock', tone: 'var(--warn)' },
    code: { icon: 'check', tone: 'var(--accent)' },
    stage: { icon: 'chart', tone: 'var(--accent)' },
    call: { icon: 'phone', tone: 'var(--warn)' },
    escalation: { icon: 'alert', tone: 'var(--bad)' },
    optout: { icon: 'x', tone: 'var(--ink-3)' },
    held: { icon: 'pause', tone: 'var(--warn)' },
    failed: { icon: 'alert', tone: 'var(--bad)' },
  };
  const { icon, tone } = meta[kind];
  return (
    <li className="activity">
      <span className="activity__icon" style={{ color: tone }}>
        <Icon name={icon} size={12} />
      </span>
      <span>
        <span className={unread ? 'activity__text activity__text--unread' : 'activity__text'}>
          {unread && <span className="activity__dot" />}
          {to ?? text}
        </span>
        <span className="micro activity__when">{when}</span>
        {action}
      </span>
    </li>
  );
}

/**
 * A trend line with a filled area under it. Built at T30 in v2's language; Reports (T32) is what
 * draws them — from counted rows, never from an estimate (design doc § T32).
 */
export function Sparkline({
  values,
  width = 120,
  height = 28,
  tone = 'var(--accent)',
  label,
}: {
  values: readonly number[];
  width?: number;
  height?: number;
  tone?: string;
  label: string;
}) {
  if (values.length < 2) return null;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const coords = values.map(
    (value, index) =>
      [
        (index / (values.length - 1)) * width,
        height - ((value - min) / range) * (height - 2) - 1,
      ] as const,
  );
  const points = coords.map(([x, y]) => `${x},${y}`).join(' ');
  // Stable per series: two sparklines on one screen must not share a gradient id.
  const gradientId = `spark-${label.replaceAll(/\W+/g, '-')}-${values.length}`;
  const last = coords.at(-1)!;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={tone} stopOpacity="0.28" />
          <stop offset="100%" stopColor={tone} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`${points} ${width},${height} 0,${height}`} fill={`url(#${gradientId})`} />
      <polyline
        points={points}
        fill="none"
        stroke={tone}
        strokeWidth="1.35"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={last[0]} cy={last[1]} r="2.2" fill={tone} />
    </svg>
  );
}

/**
 * A line of facts with a dot between each, wrapping on a narrow screen with no dot left at a
 * line's start or end (T288, 5 Oct 2026: "Remote-EU ·", "· Undo" on a phone). Each fact carries
 * its dot in front, in a gutter the outer box clips, so a fact that starts a line loses its dot.
 * Empty children (`false`, `null`, an empty string) take no place and no dot. A child keeps its
 * own key, so one that comes and goes does not remount the facts after it.
 */
export function MetaLine({ children }: { children: ReactNode }) {
  return (
    <span className="meta-line">
      <span className="meta-line__in">
        {Children.toArray(children)
          .filter((child) => child !== '')
          .map((child, i) => (
            <span key={isValidElement(child) ? child.key : i} className="meta-line__item">
              {child}
            </span>
          ))}
      </span>
    </span>
  );
}
